/**
 * Obtém o user-agent do parâmetro ou do navigator
 * @param {string} [userAgent] - User agent string (opcional)
 * @returns {string|null} User agent string ou null se não disponível
 */
const getUserAgent = (userAgent) => {
  if (userAgent) {
    return userAgent;
  }

  if (typeof navigator !== 'undefined') {
    return navigator.userAgent;
  }

  return null;
}

/**
 * Mapeamento de identificadores de user-agent para nomes de apps
 */
const IN_APP_BROWSER_PATTERNS = [
  { pattern: /whatsapp|wa4a/i, name: 'WhatsApp' },
  { pattern: /fban|fbav/i, name: 'Facebook' },
  { pattern: /instagram/i, name: 'Instagram' },
  // Twitter/X: TwitterAndroid, Twitter for iPhone/iPad
  { pattern: /TwitterAndroid|Twitter for i(Phone|Pad)/i, name: 'Twitter' },
  // LinkedIn: [LinkedInApp] ou LinkedInApp
  { pattern: /LinkedInApp/i, name: 'LinkedIn' },
  // Telegram: Telegram-Android/ ou Telegram seguido de versão
  { pattern: /Telegram(-Android|[\s/]\d)/i, name: 'Telegram' },
];

/**
 * Detecta qual app está sendo usado em caso de WebView/in-app browser
 * @param {string} userAgent - User agent string
 * @returns {string|null} Nome do app ou null se não for identificado
 */
export const detectInAppBrowserApp = (userAgent) => {
  if (!userAgent) return null;

  for (const { pattern, name } of IN_APP_BROWSER_PATTERNS) {
    if (pattern.test(userAgent)) {
      return name;
    }
  }

  return null;
};

/**
 * Detecta se o acesso está sendo feito via WebView através de flags no user-agent
 * @param {string} userAgent - User agent string
 * @returns {boolean}
 */
export const hasWebViewFlags = (userAgent) => {
  if (!userAgent) return false;

  // WebView pode ser identificado por:
  // 1. Flag "wv" como palavra completa (usando word boundary \b)
  // 2. "Version/4.0", que é um padrão comum em WebViews do Android
  const hasWvFlag = /\bwv\b/.test(userAgent);
  const hasVersion4 = userAgent.includes('Version/4.0');

  return hasWvFlag || hasVersion4;
};

/**
 * Detecta se o usuário está acessando através de in-app browsers
 * (WhatsApp, Facebook, Instagram, etc.)
 *
 * Esses navegadores internos usam WebViews que:
 * - Operam em contexto isolado (sandboxed)
 * - Não persistem permissões de câmera entre sessões
 * - Solicitam permissão novamente a cada chamada getUserMedia()
 *
 * @param {string} [userAgent] - User agent string (opcional, usa navigator.userAgent por padrão)
 * @returns {boolean} true se estiver em um in-app browser
 */
export const isInAppBrowser = (userAgent) => {
  const ua = getUserAgent(userAgent) || '';

  const appName = detectInAppBrowserApp(ua);
  if (!appName) {
    return false;
  }

  return hasWebViewFlags(ua);
};

/**
 * Obtém informações do browser formatadas para o Mixpanel (com chaves em formato de label)
 * Retorna apenas detectedApp e isWebView, pois as demais informações o Mixpanel já coleta nativamente
 * Não usa UAParser para evitar overhead desnecessário
 * @param {string} [userAgent] - User agent string (opcional, usa navigator.userAgent por padrão)
 * @returns {Object} Objeto com informações do browser formatadas para Mixpanel
 */
export const getBrowserInfoForMixpanel = (userAgent) => {
  const ua = getUserAgent(userAgent);

  return {
    'Is WebView': hasWebViewFlags(ua),
    'Detected App': detectInAppBrowserApp(ua),
  };
};
